import { mobFooterHeight, pcFooterHeight } from '@/assets/styles/var';
import { FC } from 'react';
import { styled } from 'styled-components';
import Input, { IInputRef, Mask } from './Input';
import PcInput, { TextCount } from './PcInput';
import ArrowButton from '@cps/Buttons/ArrowButton';
import Upload from '@cps/Upload';
import { EFaqKnows, useChat, useFaq, useMessageHistory } from '@/service/useChat';
import { EMessageStatus, IMessage } from '@/store/messageStore';
import useMessageStore from '@/store/messageStore';
import localforage from 'localforage';
import { localMemory } from 'localmemory';
import { v4 as uuidV4 } from 'uuid';
import { $borderRadius, $height, $paddingX, $paddingY, phoneSize } from '@/assets/styles/mediaSize';
import useAppStore from '@/store/appStore';
import { getLocalTime } from '@/utils';
import { validMsg } from '@/utils/valid';
import { useScrollThrottle } from '@/hooks/gHooks/useScroll';
import ScrollToButtom from '@cps/ScrollToBottom';
import { TipsFlag } from './Flag';
import { useTipsFlags } from './hooks/useTipsFlag';
import { Message } from '@arco-design/web-react';
import useUserStore from '@/store/userStore';
import { useUserMessage } from '@/store/messageStore';
import { SendComp } from '@/Icons';
import { errorMsg } from '@/service/serverCode';
import { useCreateChat } from '@/service/useChatConfig';
import { sessionConfigCache } from '@/store/userStore';
import { useLang } from '@/locales/useLang';
import { flexPos } from '@/assets/styles/mixin';
import { callFromExternal } from '@/utils';
import { getValue } from '@/externalConfig/conf'
const ComposerWrap = styled.div`
  min-height: ${mobFooterHeight};
  width: 100%;
  position: relative;

  @media (min-width: ${phoneSize}) {
    min-height: ${pcFooterHeight};
  }

  .composer-container {
    width: 100%;
    flex: auto;
    display: flex;
    align-items: flex-end;
    padding: 24rem;
    position: relative;

    @media (min-width: ${phoneSize}) {
      min-height: ${pcFooterHeight};
      align-items: flex-start;
    }
  }

  .composer-content {
    flex: auto;
    background-color: #fff;
    ${$height('100%', '150rem')}
    margin-left: 24rem;
    ${flexPos('space-between', 'flex-end')}
    ${$paddingY('18rem', '14rem')}
    ${$paddingX('24rem', '12rem')}
    ${$borderRadius('24rem', '12rem')}
    border: solid 1px transparent;

    &.is-focus {
      border: solid 1px #0054fc;
    }

    &.full-screen {
      background-color: transparent !important;
      border: none;
    }
  }
`;

const Composer: FC<{}> = () => {
  const composerRef = useRef<HTMLDivElement>(null);
  const [showMoreTextBtn, setShowMoreTextBtn] = useState(false);
  const inputRef = useRef<IInputRef>(null);
  const [isFullScreenInput, setIsFullScreenInput] = useState(false);
  const [msgContent, setMsgContent] = useState('');
  const { msgSend } = useChat();
  const messageStore = useMessageStore();
  const appStore = useAppStore();
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const messageContainer = useRef<HTMLElement | null>(null);
  const { countTime, isTimeout, setIsTimeout } = useTipsFlags();
  const userStore = useUserStore();
  const { otherMessage, newMsgCount } = useUserMessage();
  const [isFocus, setIsFocus] = useState(false);
  const { createSession } = useCreateChat();
  const { getFaqKnows } = useFaq();
  const { seqInfo, getSessionList } = useMessageHistory();
  const $t = useLang();

  useScrollThrottle(
    (ele) => {
      if (!ele) {
        return;
      }
      messageContainer.current = ele;

      const scrollHeight = ele.scrollHeight;
      const clientHeight = ele.clientHeight;
      const scrollTop = ele.scrollTop;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      setShowScrollBottom(distanceFromBottom > clientHeight);
    },
    100,
    '#messageContainer'
  );

  useEffect(() => {
    if (!showScrollBottom && +messageStore.unRead) {
      setTimeout(() => {
        messageContainer.current?.scrollTo({
          top: messageContainer.current.scrollHeight,
          behavior: 'smooth',
        });
      }, 199);
    }
  }, [otherMessage, messageStore.unRead]);

  useEffect(() => {
    if (isTimeout && !showScrollBottom) {
      setTimeout(() => {
        messageContainer.current?.scrollTo({
          top: messageContainer.current.scrollHeight,
          behavior: 'smooth',
        });
      }, 199);
    }
  }, [isTimeout]);

  const handleSend = async () => {
    if (appStore.isKickOff) {
      Message.info($t('chat_session_status_8'));
      return;
    }
    if (!validMsg(msgContent)) {
      Message.info($t('chat_session_status_23'));
      inputRef.current?.resetContent();
      return;
    }
    setMsgContent('');
    inputRef.current?.resetContent();
    setShowMoreTextBtn(false);

    setIsTimeout(false);

    const msgList =
      (await localforage.getItem<IMessage[]>(
        userStore.sessionConfig.uid + '__' + userStore.sessionConfig.sessionId + '-msg'
      )) || [];
    const seq = messageStore.latestSeq;

    const id = uuidV4();
    const now = getLocalTime(0).toString();

    const msgPack: IMessage = {
      msgBody: msgContent,
      msgType: 1,
      msgSeq: seq,
      status: EMessageStatus.pending,
      senderUid: userStore.sessionConfig.uid || '',
      receiverUid: '',
      createTime: now,
      updateTime: now,
      clientMsgID: id + '-' + now,
      uniqueId: id,
      sendTime: now,
      msgFromTime: 'current',
      sessionId: userStore.sessionConfig.sessionId,
    };

    const oldMsgList = messageStore.getMsgList();

    messageStore.setMessageList([...oldMsgList, msgPack]);
    // 临时保存一下刚想要发送的数据,等待真的发送成功的时候,再移除
    const tempMsgList = [...msgList, { ...msgPack, status: EMessageStatus.fail }];

    messageStore.setScrollMessage(msgList);
    await localforage.setItem(
      userStore.sessionConfig.uid + '__' + userStore.sessionConfig.sessionId + '-msg',
      tempMsgList
    );

    // 将 <br> 替换为 \n
    msgPack.msgBody = msgPack.msgBody.replace(/<br\s*\/?>/gi, '\n');

    const doSend = async () => {
      const resp = await msgSend({
        msgBody: msgPack.msgBody,
        msgSeq: msgPack.msgSeq,
        msgType: msgPack.msgType,
        clientMsgID: msgPack.clientMsgID,
        sendTime: msgPack.sendTime,
      }).catch(() => {
        return {
          success: false,
          data: { msgReq: msgPack.msgSeq },
        };
      });

      if (resp.success) {
        messageStore.setLatestSeq(resp.data?.msgReq || 0);
        msgPack.status = EMessageStatus.success;
        msgPack.msgSeq = resp.data?.msgReq!;
        messageStore.updateMessageItem(msgPack);
        const tempMsgList = [...msgList, { ...msgPack, status: EMessageStatus.success }];

        await localforage.setItem(
          userStore.sessionConfig.uid + '__' + userStore.sessionConfig.sessionId + '-msg',
          tempMsgList
        );
      } else if (
        [
          errorMsg.sessionError.code,
          errorMsg.sessionFail.code,
          errorMsg.sessionTimeout.code,
        ].includes(resp.code as any)
      ) {
        const resp = await createSession();

        const sessionList = await getSessionList();
        seqInfo.current.sessionList = sessionList;

        if (resp.code === 0) {
          msgPack.sessionId = resp.data.sessionId;
          messageStore.updateMsgSessionId(msgPack);

          await doSend();

          await getFaqKnows(EFaqKnows.knows, '');
        }
      } else {
        msgPack.status = EMessageStatus.fail;
        msgPack.msgSeq = resp.data?.msgReq!;
        messageStore.updateMessageItem(msgPack);
        const tempMsgList = [...msgList, { ...msgPack, status: EMessageStatus.fail }];

        await localforage.setItem(
          userStore.sessionConfig.uid + '__' + userStore.sessionConfig.sessionId + '-msg',
          tempMsgList
        );
      }

      return resp;
    };

    doSend();
  };
  let composerContainerClass = 'composer-container'
  const external = callFromExternal()
  if (external) {
    composerContainerClass += ' external-composer-container'
  }
  return (
    <ComposerWrap ref={composerRef} className={external ? `external-composer-wrap` : ''}>
      <ScrollToButtom
        onClick={() => {
          messageContainer.current &&
            messageContainer.current.scrollTo({
              top: messageContainer.current.scrollHeight,
              behavior: 'smooth',
            });
        }}
        show={showScrollBottom}
      >
        {+messageStore.unRead ? (
          <div>{$t('chat_session_status_11', { count: messageStore.unRead })}</div>
        ) : (
          <div>{$t('chat_session_status_10')}</div>
        )}
      </ScrollToButtom>

      <Mask show={isFullScreenInput} />

      {/* fullscreen input */}
      {appStore.curDevice === 'phone' && (
        <ArrowButton
          className={`left-32 top-28 z-10 transform ${
            isFullScreenInput ? 'fixed rotate-[-90deg]!' : 'absolute'
          }`}
          show={(showMoreTextBtn || isFullScreenInput) && !external}
          onClick={() => {
            setIsFullScreenInput(!isFullScreenInput);
            setShowMoreTextBtn(true);
          }}
        />
      )}

      <TipsFlag show={isTimeout}>
        {$t('chat_session_status_3', { count: userStore?.userReplyCountdown })}
      </TipsFlag>

      <div className={ composerContainerClass }>
        {!isFullScreenInput && <Upload />}
        <div
          className={`composer-content ${!!isFocus && 'is-focus'} ${
            isFullScreenInput ? 'full-screen' : ''
          }  ${ external ? `external-input-wrapper` : ''}`}
        >
          {appStore.curDevice === 'phone' ? (
            <Input
              ref={inputRef}
              maxWord={500}
              isFullScreen={isFullScreenInput}
              onFocus={(status) => setIsFocus(status)}
              onChange={(content) => {
                const ele = inputRef.current!.inputRef;
                const overSize = ele.scrollHeight > ele.clientHeight;
                setShowMoreTextBtn(overSize);
                setMsgContent(content);
              }}
              // onSend={(content: string) => {
              //   console.log('发送111...', content);
              // }}
            />
          ) : (
            <PcInput
              ref={inputRef}
              maxWord={500}
              isFullScreen={isFullScreenInput}
              onFocus={(status) => setIsFocus(status)}
              onChange={(content) => {
                const ele = inputRef.current!.inputRef;
                const overSize = ele.scrollHeight > ele.clientHeight;
                setShowMoreTextBtn(overSize);
                setMsgContent(content);
              }}
              onSend={() => {
                handleSend();
              }}
            />
          )}

          <div className="flex flex-col justify-between items-end h-full ml-10">
            {appStore.curDevice !== 'phone' && <TextCount count={msgContent} maxWord={500} />}
            {external ? 
            !!isFocus ? <img src={getValue('sendIconFocus')} width="20" onClick={() => handleSend()} /> : 
            <img src={getValue('sendIcon')} width="20" onClick={() => handleSend()} /> :
            <SendComp
              active={isFocus}
              className={`w-48 md:w-24 cursor-pointer ${isFullScreenInput ? 'hidden!' : ''}`}
              onClick={() => handleSend()}
            />}
          </div>
        </div>
      </div>
    </ComposerWrap>
  );
};

export default Composer;
